// "Things that need a human" block — only renders when there's
// something to see. Otherwise the dashboard stays clean.

import { useState } from 'react';
import { supabase } from '../../lib/supabaseClient';

export interface StuckRunRow {
  id: string;
  product: 'persona_builder' | 'persona_studio';
  kind: string;
  startedAt: string | null;
  ageMinutes: number;
  orgId: string;
  orgName: string;
  subjectId: string | null;
  subjectName: string | null;
}

export interface QuotaPressureRow {
  orgId: string;
  orgName: string;
  kind: 'build' | 'generate';
  used: number;
  limit: number;
  percent: number;
}

interface Props {
  stuckRuns: StuckRunRow[];
  quotaPressure: QuotaPressureRow[];
  stuckThresholdMinutes: number;
  quotaPressureThreshold: number;
  env: 'dev' | 'staging' | 'prod';
  onQuotaUpdated?: () => void;
}

const ENV_LABEL: Record<Props['env'], string> = {
  dev: 'Dev',
  staging: 'Staging',
  prod: 'Prod',
};

export function AttentionBlock({
  stuckRuns,
  quotaPressure,
  stuckThresholdMinutes,
  quotaPressureThreshold,
  env,
  onQuotaUpdated,
}: Props) {
  const nothing = stuckRuns.length === 0 && quotaPressure.length === 0;
  if (nothing) {
    return (
      <section className="border border-emerald-800/40 bg-emerald-950/20 rounded-lg p-3 flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-300">
          ✓ Nothing needs attention ({ENV_LABEL[env]})
        </span>
        <span className="text-[10px] text-slate-500">
          No stuck runs · no orgs in quota pressure
        </span>
      </section>
    );
  }

  return (
    <section className="border border-amber-800/40 bg-amber-950/10 rounded-lg overflow-hidden">
      <div className="bg-amber-900/20 px-4 py-2.5 border-b border-amber-800/40">
        <h3 className="text-[11px] font-bold uppercase tracking-widest text-amber-200">
          Needs attention ({ENV_LABEL[env]})
        </h3>
      </div>
      <div className="divide-y divide-amber-900/30">
        {stuckRuns.length > 0 && (
          <div className="px-4 py-3">
            <div className="text-[10px] uppercase tracking-widest font-bold text-amber-300 mb-2">
              {stuckRuns.length} stuck run{stuckRuns.length === 1 ? '' : 's'} (status="running" &gt;{' '}
              {stuckThresholdMinutes}m)
            </div>
            <ul className="space-y-1.5 text-xs">
              {stuckRuns.slice(0, 10).map((r) => (
                <li key={`${r.product}:${r.id}`} className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap min-w-0">
                    <span
                      className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded font-bold border ${
                        r.product === 'persona_builder'
                          ? 'bg-indigo-900/30 text-indigo-300 border-indigo-700/40'
                          : 'bg-amber-900/30 text-amber-300 border-amber-700/40'
                      }`}
                    >
                      {r.product === 'persona_builder' ? 'PB' : 'PS'}
                    </span>
                    <span className="text-[10px] font-mono text-slate-400">{r.kind}</span>
                    {r.subjectName && (
                      <>
                        <span className="text-sm font-bold text-slate-100">{r.subjectName}</span>
                        <span className="text-[10px] text-slate-500">·</span>
                      </>
                    )}
                    <span className="text-[10px] text-slate-400">{r.orgName}</span>
                  </div>
                  <div className="text-[10px] text-rose-300 font-mono whitespace-nowrap">
                    stuck {r.ageMinutes}m
                  </div>
                </li>
              ))}
              {stuckRuns.length > 10 && (
                <li className="text-[10px] text-slate-500 italic">
                  …and {stuckRuns.length - 10} more
                </li>
              )}
            </ul>
          </div>
        )}

        {/* QuotaPressureLine is defined below */}
        {quotaPressure.length > 0 && (
          <div className="px-4 py-3">
            <div className="text-[10px] uppercase tracking-widest font-bold text-amber-300 mb-2">
              {quotaPressure.length} org-quota{' '}
              {quotaPressure.length === 1 ? 'usage' : 'usages'} ≥{' '}
              {Math.round(quotaPressureThreshold * 100)}%
            </div>
            <ul className="space-y-1.5 text-xs">
              {quotaPressure.map((q) => (
                <QuotaPressureLine
                  key={`${q.orgId}:${q.kind}`}
                  row={q}
                  env={env}
                  onUpdated={onQuotaUpdated}
                />
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}

function QuotaPressureLine({
  row,
  env,
  onUpdated,
}: {
  row: QuotaPressureRow;
  env: 'dev' | 'staging' | 'prod';
  onUpdated?: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(row.limit));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const barWidth = Math.min(100, Math.round(row.percent * 100));
  const tone =
    row.percent >= 1
      ? 'bg-rose-500'
      : row.percent >= 0.9
        ? 'bg-amber-400'
        : 'bg-amber-500/70';

  async function save() {
    const n = parseInt(value, 10);
    if (!Number.isFinite(n) || n < 0) {
      setError('Enter a non-negative number');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Not authenticated');
      const body =
        row.kind === 'build'
          ? { orgId: row.orgId, buildQuota: n }
          : { orgId: row.orgId, generateQuota: n };
      const res = await fetch(`/api/admin/quota?env=${env}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${res.status}`);
      }
      setEditing(false);
      onUpdated?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <li className="flex items-center gap-3 flex-wrap">
      <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400 w-20">
        {row.kind}
      </span>
      <span className="text-sm font-bold text-slate-100 flex-1 min-w-0 truncate">
        {row.orgName}
      </span>
      <div className="flex items-center gap-2">
        <div className="w-32 h-2 bg-slate-800 rounded overflow-hidden">
          <div
            className={`h-full ${tone} transition-all`}
            style={{ width: `${barWidth}%` }}
          />
        </div>
        {editing ? (
          <div className="flex items-center gap-1">
            <span className="text-[10px] text-slate-400 font-mono">{row.used}/</span>
            <input
              type="number"
              min={0}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void save();
                if (e.key === 'Escape') {
                  setEditing(false);
                  setValue(String(row.limit));
                }
              }}
              disabled={saving}
              className="w-14 bg-slate-900 border border-slate-700 rounded px-1.5 py-0.5 text-[11px] text-slate-100 font-mono text-right"
              autoFocus
            />
            <button
              onClick={() => void save()}
              disabled={saving}
              className="text-[9px] uppercase tracking-widest font-bold text-emerald-300 hover:text-emerald-200 px-1 disabled:opacity-50"
            >
              {saving ? '…' : '✓'}
            </button>
            <button
              onClick={() => {
                setEditing(false);
                setValue(String(row.limit));
                setError(null);
              }}
              disabled={saving}
              className="text-[9px] uppercase tracking-widest font-bold text-slate-500 hover:text-slate-300 px-1"
            >
              ✕
            </button>
          </div>
        ) : (
          <button
            onClick={() => {
              setValue(String(row.limit));
              setEditing(true);
            }}
            className="text-[10px] text-slate-400 hover:text-indigo-300 font-mono whitespace-nowrap min-w-[60px] text-right"
            title="Click to change cap"
          >
            {row.used}/{row.limit} ({Math.round(row.percent * 100)}%)
          </button>
        )}
      </div>
      {error && (
        <span className="text-[10px] text-rose-300 ml-2">{error}</span>
      )}
    </li>
  );
}
